const live = (shape, nullable) => Object.freeze({ shape, nullable, commit: "live" });
/**
 * One entry per kind, exhaustive by construction.
 *
 * `nullable` says whether the field may hold `null` — whether *absence* is one of its values. Eleven
 * kinds say no: `checkbox`, `colors`, `daterange`, `email`, `file`, `multiselect`, `password`,
 * `slider`, `text`, `textarea` and `toggle`. Each of them has an empty value that is a real value of
 * its type — `false`, `""`, `[]`, `{start: null, end: null}` — which is what
 * {@link mdyEmptyValueFor} answers from the other direction.
 *
 * **It does not say whether a `required` can fail.** That was the reading this comment invited and it
 * is wrong in both directions: `checkbox` and `toggle` are not nullable and their `required` refuses
 * `false`, which is the whole point of a consent field, and `text` refuses `""`. The one kind whose
 * `required` has nothing to refuse is `slider` — a thumb is always somewhere, so there is no state
 * for it to be empty in. Ask emptiness of `required`; ask `nullable` whether `null` is a value the
 * field may hold.
 */
export const MDY_VALUE_CONTRACTS = Object.freeze({
    text: live("string", false),
    textarea: live("string", false),
    email: live("string", false),
    password: Object.freeze({ ...live("string", false), concealed: true }),
    // The hex box is what the label names and what a keyboard types into, and it writes on blur or
    // Enter rather than per keystroke: `#11` is not a colour, and a field that took it would hold a
    // value nothing could show. The native swatch beside it writes as soon as it is used — one word
    // per kind, and it answers for the control a person can leave half-finished.
    colors: Object.freeze({ shape: "string", nullable: false, commit: "confirm" }),
    number: live("number", true),
    // A thumb is always somewhere, so a slider always holds a number.
    slider: live("number", false),
    checkbox: live("boolean", false),
    toggle: live("boolean", false),
    select: live("option", true),
    radio: live("option", true),
    segmented: live("option", true),
    multiselect: live("option[]", false),
    datepicker: live("string", true),
    // The picker edits a draft on its dial and writes nothing until the user confirms, so an abandoned
    // picker leaves the field exactly as it found it.
    timepicker: Object.freeze({ shape: "string", nullable: true, commit: "confirm" }),
    // Neither live nor confirm: the first endpoint writes nothing because a start with no end is not a
    // range, and the second writes both. There is no OK to press.
    daterange: Object.freeze({ shape: "dateRange", nullable: false, commit: "complete" }),
    file: live("file[]", false),
});
/** Whether a value matches the shape its kind declares. */
export function matchesValueShape(shape, value) {
    switch (shape) {
        case "string":
            return typeof value === "string";
        case "number":
            return typeof value === "number" && Number.isFinite(value);
        case "boolean":
            return typeof value === "boolean";
        // An option's value is whatever the option list holds, so anything non-nullish satisfies the
        // shape. That the value is *one of the offered options* is a validator's question, and
        // `oneOf`/`eachOneOf` already answer it.
        case "option":
            return value !== null && value !== undefined;
        case "option[]":
            return Array.isArray(value);
        case "dateRange":
            return typeof value === "object" && value !== null && "start" in value && "end" in value;
        case "file[]":
            return Array.isArray(value);
    }
}
/**
 * Why a value does not belong in a field of this kind, or `null` if it does.
 *
 * Returns the reason rather than a boolean so a failure names what was wrong, which is the
 * difference between a check that reports a defect and one that only reports a colour.
 */
export function explainValueMismatch(kind, value) {
    const contract = MDY_VALUE_CONTRACTS[kind];
    if (value === null || value === undefined) {
        return contract.nullable ? null : `${kind} cannot hold ${String(value)}`;
    }
    if (!matchesValueShape(contract.shape, value)) {
        // NaN and the infinities are refused for their value, not their type, so naming the type would
        // answer "got number" where a number is exactly what was asked for.
        const got = typeof value === "object" ? JSON.stringify(value)
            : typeof value === "number" && !Number.isFinite(value) ? String(value) : typeof value;
        return `${kind} holds ${contract.shape}, got ${got}`;
    }
    return contentMismatch(kind, value);
}
/**
 * Why a value of the right *shape* is still not one this kind can hold.
 *
 * Three kinds carry a string with a form: a date is ISO `yyyy-MM-dd`, a time is `HH:mm`, and a range
 * is two of the first. The shape check cannot see it — `string` is `string` — so a value arriving
 * from outside the control, a tampered draft above all, was taken whole: a datepicker restored from
 * storage held `"not a date at all"` and the form called itself valid and submittable.
 *
 * Said as a reason rather than refused here: the engine reports a shape it does not expect as a
 * verdict, which is what lets a field show what a person typed and say why it is wrong.
 */
function contentMismatch(kind, value) {
    // The empty string is how absence is written wherever a value is a formatted string — `email` and
    // `pattern` both pass on it, and a document writing `initialValue: ""` for a date means "none".
    // Refusing it here would drop the field, which is a heavier answer than the mistake.
    if (value === "")
        return null;
    if (kind === "datepicker") {
        return isIsoDate(value) ? null : `${kind} holds an ISO date (yyyy-MM-dd), got ${JSON.stringify(value)}`;
    }
    if (kind === "timepicker") {
        return typeof value === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
            ? null
            : `${kind} holds a time (HH:mm), got ${JSON.stringify(value)}`;
    }
    if (kind === "colors") {
        return isHexColor(value) ? null : `${kind} holds a hex colour (#rgb or #rrggbb), got ${JSON.stringify(value)}`;
    }
    if (kind === "daterange") {
        const range = value;
        for (const end of ["start", "end"]) {
            const held = range[end];
            if (held === null || held === undefined || isIsoDate(held))
                continue;
            return `${kind} holds ISO dates, and ${end} is ${JSON.stringify(held)}`;
        }
        return null;
    }
    return null;
}
/**
 * A colour this field can hold: `#rgb` or `#rrggbb`, in either case.
 *
 * Stated here because the field's own control already refuses anything else — typing `banana` leaves
 * the value as it was — and a rule that lives only on the typed path makes the verdict depend on
 * which door the value came through. A colour restored from a draft used to be taken whole: the
 * model held `banana`, the swatch fell back to black, and the form sent what the page never showed.
 */
function isHexColor(value) {
    return typeof value === "string" && /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(value);
}
/** ISO `yyyy-MM-dd`, and a real date rather than a well-shaped impossible one. */
function isIsoDate(value) {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return false;
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}
